import {
  BookIcon,
  Box,
  BulbSuggestionIcon,
  DescriptionIcon,
  InformationIcon,
  Popover,
  PopoverTrigger,
  Portal,
  SparkleIcon,
  useDisclosure,
} from "@hightouch/htui";
import { ElementType } from "react";
import { NavItem } from "src/components/layout/nav/nav-item";
import { navIcon } from "src/components/layout/nav/nav-icon";
import { useUser } from "src/contexts/user-context";
import * as analytics from "src/lib/analytics";
import { newPylonMessage } from "src/lib/pylon";
import {
  NAV_POPOVER_MENU_ITEM_STYLES,
  NavPopoverContentAndBody,
  PopoverHeader,
} from "src/components/layout/nav/nav-popovers";

const ResourcesIcon = navIcon(BookIcon);

export const Resources = () => {
  const { workspace } = useUser();
  const { isOpen, onClose, onToggle } = useDisclosure();

  const trackDocsClicked = () => {
    analytics.track("Docs Clicked", {
      workspace_id: workspace?.id,
    });
  };

  const trackStatusClicked = () => {
    analytics.track("Status Page Clicked", {
      workspace_id: workspace?.id,
    });
  };

  const trackChangelogClicked = () => {
    analytics.track("Changelog Clicked", {
      workspace_id: workspace?.id,
    });
  };

  const submitFeedback = () => {
    // Event name pinned to master's value to avoid breaking analytics; the
    // Pylon prefill matches the current "Feedback" label.
    analytics.track("Feedback Menu: Submit feature request clicked.", {
      workspace_id: workspace?.id,
    });

    newPylonMessage("Hi, I have some feedback.");
  };

  return (
    <Popover
      placement="right-start"
      offset={[0, 16]}
      isOpen={isOpen}
      onClose={onClose}
    >
      <PopoverTrigger>
        <NavItem
          isActive={isOpen}
          icon={ResourcesIcon}
          label="Resources"
          onClick={onToggle}
        />
      </PopoverTrigger>
      <Portal>
        <NavPopoverContentAndBody>
          <PopoverHeader title="Resources" />
          <MenuRow
            href={import.meta.env.VITE_DOCS_URL}
            icon={DescriptionIcon}
            label="Documentation"
            onClick={trackDocsClicked}
          />
          <MenuRow
            href="https://status.hightouch.io"
            icon={InformationIcon}
            label="System status"
            onClick={trackStatusClicked}
          />
          <MenuRow
            href="https://hightouch.com/docs/changelog"
            icon={SparkleIcon}
            label="Product updates"
            onClick={trackChangelogClicked}
          />
          <MenuRow
            icon={BulbSuggestionIcon}
            label="Feedback"
            onClick={submitFeedback}
          />
        </NavPopoverContentAndBody>
      </Portal>
    </Popover>
  );
};

type MenuRowProps = {
  icon: ElementType;
  label: string;
  onClick: () => void;
  href?: string;
};

const MenuRow = ({ icon: Icon, label, onClick, href }: MenuRowProps) => (
  <Box
    as={href ? "a" : "button"}
    {...(href ? { href, target: "_blank", rel: "noopener noreferrer" } : {})}
    display="flex"
    alignItems="center"
    gap={2}
    bg="white"
    color="text.primary"
    h={10}
    _hover={{ bg: "base.background" }}
    onClick={onClick}
    {...NAV_POPOVER_MENU_ITEM_STYLES}
  >
    <Box as={Icon} fontSize="lg" color="text.secondary" flexShrink={0} />
    {label}
  </Box>
);
